import * as _ from 'underscore';
import { IServerSettingsScope } from '../../settings/controllers/ServerSettingsFormCtrl';
import { IServerAdminSettings, ISiteSettingsForServerAdmin } from '../types/ISite';
import { ISiteSettingsForSiteAdmin } from '../types/ISiteSettingsForSiteAdmin';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { Module } from '../../ts/Module';
import { Sites } from '../../ts/siteManagement/services/resources/Sites';
import { IDashExtServerSettings as IExtensionsServerSettings, IExtsvcServerSettings } from '../../ts/ServerApi/types/IEditableServerSettings';
import { IError } from '../../httpClient/serverApi/types/IError';
import { SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { ISiteAuthenticationSettings } from '../siteAuthSettings/services/SiteAuthenticationSettingsService';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';

/**
* This directive allows us to coordinate server and site settings form
* controls in both single site (site and server settings combined) and multisite
* (site and server settings separated).
*
* The coordinator handles UI feedback for all successes and will show and error message
* if any settings fails to save. Specific form controllers should handle form-specific
* error reporting.
*/

export interface IForm {
  name: string;
  state: ISettingsFormScope;
}

export interface ISettingsFormScope  {
  busy?: boolean;
  formController: Omit<ng.IFormController, '$getControls'>;
  cancel: () => void;
  clearInvalid?: (name: string) => void;
  close?: () => void;
  focusByName?: (name: string) => void;
  isBusy?: () => boolean;
  isFormUnchanged?: () => boolean;
  isValid?: () => boolean;
  onSave?: () => ng.IPromise<any>;
  resetForm?: () => void;
  save: () => ng.IPromise<any>;
  showResetBtn?: () => void;
  resetSettings?: () => void;
  updateFailure?: (errors: IError[], site: ISiteSettingsForServerAdmin) => ng.IPromise<any>;
}

export interface ICoordinatedForm {
  state: ISettingsFormScope;
  registerFormStateByName: (state: ISettingsFormScope, name: string) => void;
  clearForms: () => void;
}

export interface ISettingsTabsCtrlScope extends ng.IScope {
  extensionsSettingsTabEnabled: boolean;
  customizationSettingsTabEnabled: boolean;
  hideNameAndId: boolean;
  showRemoteAgentsSection: boolean;
  server: SessionManagementService;
  showEncryptionStatus: boolean; // This is to control whether or not the extract encryption status shows up. It is only hidden in the create site dialog.
  createSite?: boolean;
}

export interface ISettingsCoordinatorCtrlScope<T = ISiteSettingsForSiteAdmin> extends ISettingsTabsCtrlScope, IServerSettingsScope {
  settingsCoordinatorForm: ng.IFormController; // from settingsCoordinator.pug
  sharedForm: ICoordinatedForm;
  localForm: ISettingsFormScope;
  serverSettings?: IServerAdminSettings;
  siteSettingsForServerAdmin: ISiteSettingsForServerAdmin;
  siteSettings: T;
  siteAuthenticationSettings: ISiteAuthenticationSettings;
  extensionsServerSettings: IExtensionsServerSettings;
  extsvcServerSettings: IExtsvcServerSettings;
  isResponsivePage: boolean;
  siteId?: string;
  showResetSettingsBtn: () => boolean;
}


export class SettingsFormService implements ICoordinatedForm {

  public state: ISettingsFormScope;
  private forms: ISettingsFormScope[];


  public static $inject = [
    '$q',
    '$translate',
    '$timeout'
  ];

  constructor(
    private $q: ng.IQService,
    $translate: ng.translate.ITranslateService,
    $timeout: ng.ITimeoutService
  ) {
    const toaster = ToasterService.instance;
    this.forms = [];
    this.state =  {
      formController: null,
      busy: false,
      isBusy: () => _.some(this.forms, (form: ISettingsFormScope) => form.isBusy()),
      isFormUnchanged: () => _.every(this.forms, (form: ISettingsFormScope) =>
        form.isFormUnchanged()),
      isValid: () => _.every(this.forms,
        (form: ISettingsFormScope) =>
          form.formController ? form.formController.$valid : true),
      save: () => {
        const savePromises =  _.map(this.forms, (form: ISettingsFormScope) => form.save());
        return this.$q.all(savePromises).then((result: any) => {
          if (Array.isArray(result)){
            for(let r of result){
              if (ResultHelper.hasErrors(r)){
                toaster.pop({ type: ToastType.Error, text: $translate.instant('settingsUpdateFailure') });
                return;
              }
            }
          }
          if (ResultHelper.hasErrors(result)){
            // TODO: show error ids in toast
            const errorIds = ResultHelper.getErrorIds(result);
            toaster.pop({ type: ToastType.Error, text: $translate.instant('settingsUpdateFailure') });
          }
          else {
            toaster.pop({ type: ToastType.Success, text: $translate.instant('settingsUpdateSuccess') });
          }
        });
      },
      cancel: () => {
        _.each(this.forms, (form: ISettingsFormScope) => {
          if (form.cancel) form.cancel();
        });
        toaster.pop({ type: ToastType.Info, text: $translate.instant('settingsReverted') });
      },
      clearInvalid(inputName: string): void {
        _.each(this.forms, (form: ISettingsFormScope) => {
          const ngModelCtrl: ng.INgModelController = (<any>form.formController)[inputName];
          if (ngModelCtrl.$invalid) {
            ngModelCtrl.$setViewValue(null);
            ngModelCtrl.$render();
            ngModelCtrl.$setPristine();
            _.each(_.keys(ngModelCtrl.$error), (error) => ngModelCtrl.$setValidity(error, true));
          }
        });
      },
      focusByName: (name) => {
        // Timeout allows field to be enabled first
        $timeout(() => angular.element('input[name="'+name+'"], textarea[name="'+name+'"]').focus());
      },
      onSave: () => {
        const onSavePromises = _.map(this.forms, (form: ISettingsFormScope) => {
          if (form.onSave) return form.onSave();
        });
        return this.$q.all(onSavePromises);
      },
      resetForm: () => {
        _.each(this.forms, (form: ISettingsFormScope) => {
          if (form.resetForm) form.resetForm();
        });
      },
      resetSettings: () => {
        _.each(this.forms, (form: ISettingsFormScope) => {
          if (form.resetSettings) form.resetSettings();
        });
      },
      updateFailure: (errors: IError[], site: ISiteSettingsForServerAdmin) => {
        // Handles errors in site settings coordinator and edit site modal and create site modal
        _.each(errors, (error: IError) => {
          ToasterService.instance.pop({ type: ToastType.Error, text: Sites.instance.errorMessage(error.code, site)});
        });
        return this.$q.when();
      }
    };

  }

  public registerFormStateByName(state: ISettingsFormScope, name: string) {
    this.forms.push(state);
  }

  public clearForms() {
    this.forms = [];
  }

  public showResetSettingsBtn = () => _.some(this.forms, (form: ISettingsFormScope) => !!form.resetSettings);


}

Module.instance().service('SettingsFormService', SettingsFormService);

export class SettingsCoordinatorCtrl {

  public static $inject = [
    '$scope',
    'SettingsFormService',
    '$location'
  ];

  constructor(
    private scope: ISettingsCoordinatorCtrlScope,
    private formService: SettingsFormService,
    private location: ng.ILocationService,
    siteSettings: ISiteSettingsForSiteAdmin, // from angular ui state resolve to be available for child scopes
    siteSettingsForServerAdmin: ISiteSettingsForServerAdmin,
    extensionsServerSettings: IExtensionsServerSettings // only resolved for settings.extensions states
  ) {
    this.formService.state.formController = this.formService.state.formController
      || (scope as any).settingsCoordinatorForm;
    scope.sharedForm = scope.sharedForm || this.formService;
    this.formService.clearForms();
    this.scope.showResetSettingsBtn = this.formService.showResetSettingsBtn;
    this.scope.isResponsivePage = this.isResponsivePage();
    this.scope.$on('destroy', () => {
      this.formService.clearForms();
    });
  }

  /**
   * Used to break out the template from a fixed width
   */
  private isResponsivePage(): boolean {
    return this.location.url().includes('server/customization');
  }
}

Module.instance().directive('tbSettingsCoordinator',
  () => {
    return {
      restrict: 'E',
      replace: true,
      transclude: true,
      scope: true,
      templateUrl: 'settingsCoordinator.html',
      link: ($scope: ISettingsCoordinatorCtrlScope) =>  {
        $scope.sharedForm.state.formController = $scope.settingsCoordinatorForm;
      },
      controller: SettingsCoordinatorCtrl
    };
  }
);
